import React, { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';
import Auth from './components/Auth';
import FlightFormSidebar from './components/FlightFormSidebar';
import FlightListNew from './components/FlightListNew';
import StatusSummaryNew from './components/StatusSummaryNew';
import Globe from './components/Globe';
import { Button } from './components/ui/button';
import { Dialog, DialogContent } from './components/ui/dialog';
import { LogOut, User } from 'lucide-react';
import { useAirports } from './contexts/AirportsContext';

function AppNew() {
  const { airports } = useAirports();
  const [session, setSession] = useState(null);
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flightsLoading, setFlightsLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [currentStatusPoints, setCurrentStatusPoints] = useState(0);
  const [atmosSummitBonus, setAtmosSummitBonus] = useState(false);

  useEffect(() => {
    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        setShowAuth(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    // Load user profile and flights when user is logged in and airports are loaded
    if (session?.user && airports.length > 0) {
      loadUserProfile();
      loadFlights();
    } else if (!session?.user) {
      // Reset current points when logged out
      setCurrentStatusPoints(0);
    }
  }, [session, airports]);

  const loadUserProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('current_status_points')
        .eq('user_id', session.user.id)
        .single();

      if (error) {
        // If profile doesn't exist, create it
        if (error.code === 'PGRST116') {
          const { data: newProfile, error: insertError } = await supabase
            .from('user_profiles')
            .insert({ user_id: session.user.id, current_status_points: 0 })
            .select()
            .single();

          if (insertError) throw insertError;
          setCurrentStatusPoints(0);
        } else {
          throw error;
        }
      } else {
        setCurrentStatusPoints(data.current_status_points || 0);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const loadFlights = async () => {
    setFlightsLoading(true);
    try {
      const { data, error } = await supabase
        .from('flights')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Convert Supabase flights to the format used by the app
      // Look up full airport data from local airports list
      const formattedFlights = data.map((flight) => {
        const originAirport = airports.find(a => a.iata_code === flight.origin_code);
        const destAirport = airports.find(a => a.iata_code === flight.destination_code);

        return {
          id: flight.id,
          origin: {
            code: flight.origin_code,
            name: originAirport?.name || '',
            city: originAirport?.city || '',
            latitude: originAirport?.latitude || 0,
            longitude: originAirport?.longitude || 0,
          },
          destination: {
            code: flight.destination_code,
            name: destAirport?.name || '',
            city: destAirport?.city || '',
            latitude: destAirport?.latitude || 0,
            longitude: destAirport?.longitude || 0,
          },
          fare_class: flight.fare_class,
          custom_multiplier: flight.custom_multiplier || null,
          distance_miles: parseFloat(flight.distance_miles),
          status_points: flight.status_points,
        };
      });

      setFlights(formattedFlights);
    } catch (error) {
      console.error('Error loading flights:', error);
    } finally {
      setFlightsLoading(false);
    }
  };

  const handleFlightAdded = async (flight) => {
    // Add to local state - use functional update to avoid race conditions
    setFlights(prevFlights => [...prevFlights, flight]);

    // Save to Supabase if logged in
    if (session?.user) {
      try {
        await supabase.from('flights').insert({
          user_id: session.user.id,
          origin_code: flight.origin.code,
          destination_code: flight.destination.code,
          fare_class: flight.fare_class,
          custom_multiplier: flight.custom_multiplier || null,
          distance_miles: flight.distance_miles,
          status_points: flight.status_points,
        });
      } catch (error) {
        console.error('Error saving flight:', error);
      }
    }
  };

  const handleDeleteFlight = async (index) => {
    const flight = flights[index];

    // Remove from local state
    setFlights(flights.filter((_, i) => i !== index));

    // Delete from Supabase if logged in and flight has an ID
    if (session?.user && flight.id) {
      try {
        await supabase.from('flights').delete().eq('id', flight.id);
      } catch (error) {
        console.error('Error deleting flight:', error);
      }
    }
  };

  const handleFlightFlown = async (index) => {
    const flight = flights[index];

    // Add flight's points to current status points
    const newCurrentPoints = currentStatusPoints + flight.status_points;
    setCurrentStatusPoints(newCurrentPoints);

    // Update Supabase user profile with new current points
    if (session?.user) {
      try {
        await supabase
          .from('user_profiles')
          .update({ current_status_points: newCurrentPoints })
          .eq('user_id', session.user.id);
      } catch (error) {
        console.error('Error updating current points:', error);
      }
    }

    // Delete the flight (same as handleDeleteFlight)
    handleDeleteFlight(index);
  };

  const handleUpdateCurrentPoints = async (newValue) => {
    const parsedValue = parseInt(newValue, 10);
    if (isNaN(parsedValue) || parsedValue < 0) return;

    setCurrentStatusPoints(parsedValue);

    // Update Supabase
    if (session?.user) {
      try {
        await supabase
          .from('user_profiles')
          .update({ current_status_points: parsedValue })
          .eq('user_id', session.user.id);
      } catch (error) {
        console.error('Error updating current points:', error);
      }
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setFlights([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background text-foreground">
      {/* Header */}
      <header className="bg-card border-b border-border px-6 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-1">
          <img src="/logo.png" alt="Atmos Logo" className="h-10 w-10" />
          <h1 className="text-xl font-bold text-primary">Atmos Status Mapper</h1>
        </div>
        <div className="flex items-center gap-4">
          {session ? (
            <>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-4 w-4" />
                <span>{session.user.email}</span>
              </div>
              <Button onClick={handleSignOut} variant="outline" size="sm">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </>
          ) : (
            <Button onClick={() => setShowAuth(true)} variant="outline" size="sm">
              <User className="h-4 w-4 mr-2" />
              Login
            </Button>
          )}
        </div>
      </header>

      {/* Auth Modal */}
      <Dialog open={showAuth && !session} onOpenChange={setShowAuth}>
        <DialogContent>
          <Auth onAuthChange={() => {}} />
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Left Sidebar - Form + Status + Flight List */}
        <aside className="w-full md:w-80 bg-card border-r border-border flex flex-col shrink-0">
          {/* Flight Form */}
          <FlightFormSidebar onFlightAdded={handleFlightAdded} />

          {/* Status Summary */}
          <div className="border-b border-border">
            <StatusSummaryNew
              flights={flights}
              loading={flightsLoading}
              currentStatusPoints={currentStatusPoints}
              onUpdateCurrentPoints={handleUpdateCurrentPoints}
              isLoggedIn={!!session}
              atmosSummitBonus={atmosSummitBonus}
              onAtmosSummitBonusChange={setAtmosSummitBonus}
            />
          </div>

          {/* Flight List */}
          <div className="flex-1 overflow-y-auto">
            <FlightListNew
              flights={flights}
              onDeleteFlight={handleDeleteFlight}
              onFlightFlown={handleFlightFlown}
              loading={flightsLoading}
              isLoggedIn={!!session}
            />
          </div>
        </aside>

        {/* Globe Area - Hidden on mobile */}
        <main className="hidden md:block flex-1 relative">
          <Globe flights={flights} />
        </main>
      </div>
    </div>
  );
}

export default AppNew;
